import { images } from "./images";

export const services = [
  {
    slug: "uniformed-security-guards",
    name: "Uniformed Security Guards",
    short: "Visible, professional on-site guards for commercial, residential and public properties.",
    angle: "A uniformed presence remains the single most effective visible deterrent against trespass, theft and disorder on any property.",
    deliverables: [
      "Static post coverage for lobbies, gates and perimeters",
      "Foot patrols with digital checkpoint verification",
      "Access control and visitor management",
      "Incident reporting with time-stamped daily logs",
      "Emergency response and first-aid-certified personnel",
      "Detailed shift handover and supervisor audits",
    ],
    keywords: ["uniformed security guards", "on-site security guards", "licensed security guards", "static security guard services", "professional security officers"],
    image: images.guard,
  },
  {
    slug: "mobile-security-patrol",
    name: "Mobile Security Patrol",
    short: "Marked vehicle patrols that inspect multiple sites on randomized, verified schedules.",
    angle: "Mobile patrol gives wide-area properties the deterrence of a guard presence at a fraction of the cost of a full-time static post.",
    deliverables: [
      "Randomized and scheduled patrol routes",
      "GPS-verified checkpoint scans on every visit",
      "Lock checks, gate inspections and perimeter sweeps",
      "Rapid alarm and incident response",
      "Photographic reporting after each patrol",
      "Vacant property and construction yard checks",
    ],
    keywords: ["mobile security patrol", "vehicle patrol services", "mobile patrol security", "patrol security company", "property patrol checks"],
    image: images.patrol,
  },
  {
    slug: "construction-site-security",
    name: "Construction Site Security",
    short: "Guarding for active builds, laydown yards and development sites against theft and vandalism.",
    angle: "Tool, copper and equipment theft cost Canadian builders millions every year — a guarded site is a site that keeps working on schedule.",
    deliverables: [
      "Overnight and weekend site guarding",
      "Delivery gate control and driver verification",
      "Equipment and material inventory checks",
      "Trespasser deterrence and removal protocols",
      "Fire watch during hot-work operations",
      "Daily site condition and hazard reporting",
    ],
    keywords: ["construction site security", "construction security guards", "building site security services", "job site theft prevention", "construction site patrol"],
    image: images.construction,
  },
  {
    slug: "commercial-office-security",
    name: "Commercial & Office Building Security",
    short: "Lobby guards, access control and tenant safety programs for offices and commercial towers.",
    angle: "Office towers need security that protects people and property while presenting a polished, welcoming front-of-house image.",
    deliverables: [
      "Lobby and reception security posts",
      "Access card systems and visitor sign-in",
      "Tenant escort and after-hours lock-up",
      "Loading dock and mailroom monitoring",
      "Emergency evacuation floor wardens",
      "Incident reporting for property management",
    ],
    keywords: ["office building security", "commercial security guards", "corporate security services", "lobby security", "building security company"],
    image: images.commercial,
  },
  {
    slug: "retail-loss-prevention",
    name: "Retail Loss Prevention",
    short: "Plain-clothes and uniformed loss prevention specialists for stores and shopping centres.",
    angle: "Shrinkage is a margin problem before it is a crime problem — professional loss prevention pays for itself in recovered inventory.",
    deliverables: [
      "Plain-clothes loss prevention officers",
      "Uniformed deterrence at entrances and exits",
      "Shoplifting apprehension within provincial law",
      "CCTV-assisted floor surveillance",
      "Internal theft investigation support",
      "Shrinkage reporting and trend analysis",
    ],
    keywords: ["retail loss prevention", "loss prevention officers", "retail security guards", "shoplifting prevention services", "store security company"],
    image: images.surveillance,
  },
  {
    slug: "event-security",
    name: "Event Security Services",
    short: "Crowd management, access screening and licensed guards for events of every size.",
    angle: "Great event security is invisible when things go right and decisive in the seconds when they don't.",
    deliverables: [
      "Crowd management and queue control",
      "Ticket and credential verification",
      "Bag checks and magnetometer screening",
      "Backstage and VIP area protection",
      "Licensed special-event guards",
      "Post-event debrief and incident logs",
    ],
    keywords: ["event security services", "event security guards", "crowd management services", "festival security company", "concert security guards"],
    image: images.guard,
  },
  {
    slug: "concierge-front-desk-security",
    name: "Concierge & Front Desk Security",
    short: "Hospitality-trained guards who combine resident service with building protection.",
    angle: "A concierge guard is both the face of your building and its first line of defence — service-minded, observant and always on post.",
    deliverables: [
      "Front desk staffing and resident assistance",
      "Package and delivery management",
      "Visitor screening and key control",
      "Amenity booking and move-in coordination",
      "CCTV and alarm panel monitoring",
      "Nightly building patrols and lock checks",
    ],
    keywords: ["concierge security", "front desk security guards", "residential concierge services", "condo security concierge", "building concierge security"],
    image: images.commercial,
  },
  {
    slug: "industrial-warehouse-security",
    name: "Industrial & Warehouse Security",
    short: "Gatehouse, yard and inventory protection for warehouses, plants and distribution centres.",
    angle: "Industrial sites carry concentrated value — inventory, machinery and fuel — that demands layered physical protection around the clock.",
    deliverables: [
      "Gatehouse and weigh-scale access control",
      "Trailer and container seal verification",
      "Yard patrols and fence-line inspections",
      "Shipping and receiving oversight",
      "Hazard observation and safety reporting",
      "Overnight and holiday coverage",
    ],
    keywords: ["warehouse security", "industrial security guards", "distribution centre security", "gatehouse security services", "factory security company"],
    image: images.construction,
  },
  {
    slug: "healthcare-facility-security",
    name: "Healthcare Facility Security",
    short: "De-escalation-trained guards for hospitals, clinics and long-term care homes.",
    angle: "Healthcare security demands a rare balance: compassion under pressure and absolute control when violence threatens patients or staff.",
    deliverables: [
      "Emergency department presence and de-escalation",
      "Patient watch and elopement prevention",
      "Parking and exterior patrols",
      "Access control for restricted units",
      "Code white and emergency response support",
      "Incident documentation for health authorities",
    ],
    keywords: ["hospital security guards", "healthcare security services", "clinic security company", "long-term care security", "medical facility security"],
    image: images.surveillance,
  },
  {
    slug: "residential-condo-security",
    name: "Residential & Condo Security",
    short: "Courtesy patrols, concierge coverage and amenity protection for residential communities.",
    angle: "Residents notice security most when it's missing — consistent, courteous guarding turns a building into a community people trust.",
    deliverables: [
      "Nightly common-area and parking patrols",
      "Concierge and visitor management",
      "Amenity and pool area supervision",
      "Noise and bylaw complaint response",
      "Package room security",
      "Strata and property-manager reporting",
    ],
    keywords: ["condo security guards", "residential security services", "apartment security patrol", "courtesy patrol services", "strata security company"],
    image: images.commercial,
  },
  {
    slug: "vip-executive-protection",
    name: "VIP & Executive Protection",
    short: "Discreet close protection for executives, public figures and high-profile visitors.",
    angle: "Executive protection is planning made visible — advance work, route intelligence and calm professionals who never become the story.",
    deliverables: [
      "Close protection agents and details",
      "Advance site and route assessment",
      "Secure ground transportation coordination",
      "Event and venue arrival management",
      "Travel risk briefings",
      "Confidential, low-profile operations",
    ],
    keywords: ["executive protection services", "vip security guards", "close protection agents", "bodyguard services", "corporate executive security"],
    image: images.guard,
  },
  {
    slug: "cctv-remote-monitoring",
    name: "24/7 CCTV & Remote Monitoring",
    short: "Live camera monitoring and alarm verification backed by human operators, day and night.",
    angle: "Cameras record; monitored cameras respond — live operators turn passive footage into real-time intervention.",
    deliverables: [
      "Live video monitoring by trained operators",
      "Alarm verification and dispatch",
      "Virtual guard tours on schedule",
      "Two-way audio intervention and warnings",
      "Incident clips and evidence packaging",
      "System health checks and uptime reporting",
    ],
    keywords: ["cctv monitoring services", "remote video monitoring", "24/7 security monitoring", "live camera monitoring", "virtual security guard services"],
    image: images.surveillance,
  },
  {
    slug: "fire-watch-security",
    name: "Fire Watch Guards",
    short: "Code-compliant fire watch when alarms, sprinklers or suppression systems are offline.",
    angle: "When a fire system goes down, a documented fire watch keeps your building occupied, compliant and insured.",
    deliverables: [
      "Continuous patrols of affected areas",
      "Time-stamped fire watch logs for authorities",
      "Hot-work supervision and stand-by",
      "Immediate emergency notification protocols",
      "Coordination with fire departments and inspectors",
      "Documentation for insurance and AHJ requirements",
    ],
    keywords: ["fire watch services", "fire watch guards", "fire watch security company", "hot work fire watch", "emergency fire watch"],
    image: images.patrol,
  },
  {
    slug: "traffic-control-parking-enforcement",
    name: "Traffic Control & Parking Enforcement",
    short: "Certified flaggers, parking enforcement and vehicle flow management.",
    angle: "Controlled traffic keeps sites safe, legal and moving — certified people with the right signage make it happen.",
    deliverables: [
      "Certified traffic control persons (TCP)",
      "Parking lot enforcement and ticketing",
      "Event and construction vehicle routing",
      "Road closure and detour management",
      "Delivery dock scheduling",
      "Daily traffic and incident logs",
    ],
    keywords: ["traffic control services", "parking enforcement company", "flagging services", "traffic control persons", "parking lot security"],
    image: images.patrol,
  },
  {
    slug: "alarm-response-keyholding",
    name: "Alarm Response & Keyholding",
    short: "Rapid response to alarm activations with secure keyholding and site attendance.",
    angle: "An alarm without a responder is just noise — keyholding puts a trained guard at your door within minutes, not hours.",
    deliverables: [
      "24/7 alarm response dispatch",
      "Secure keyholding with audited key control",
      "Interior and exterior premise checks",
      "False-alarm reset and system coordination",
      "Lock and unlock services for staff",
      "Response-time reporting on every call",
    ],
    keywords: ["alarm response services", "keyholding services", "security alarm response", "key holding company", "rapid alarm response security"],
    image: images.patrol,
  },
  {
    slug: "crisis-strike-security",
    name: "Crisis Management & Strike Security",
    short: "Calm, lawful security coverage for labour disputes, emergencies and business disruption.",
    angle: "Labour disputes and crises demand restraint above all — disciplined guards who document everything and escalate nothing.",
    deliverables: [
      "Picket-line and gate monitoring",
      "Evidence-grade video and incident logs",
      "Escort services for staff and management",
      "Access control during work stoppages",
      "Business continuity site protection",
      "Liaison with police and legal counsel",
    ],
    keywords: ["strike security services", "labour dispute security", "crisis management security", "emergency security guards", "picket line security"],
    image: images.guard,
  },
  {
    slug: "school-campus-security",
    name: "School & Campus Security",
    short: "Safeguarding-focused guards for schools, colleges and university campuses.",
    angle: "Campus security works when guards are approachable mentors in the hallway and unshakeable professionals in an emergency.",
    deliverables: [
      "Perimeter and entrance monitoring",
      "Visitor management and sign-in",
      "Event and exam-period coverage",
      "Residence and parking patrols",
      "Emergency lockdown support",
      "Safeguarding-trained, background-checked staff",
    ],
    keywords: ["school security guards", "campus security services", "university security company", "college campus security", "education facility security"],
    image: images.commercial,
  },
  {
    slug: "hotel-hospitality-security",
    name: "Hotel & Hospitality Security",
    short: "Guest-first security for hotels, resorts, restaurants and nightlife venues.",
    angle: "Hospitality security protects the guest experience as much as the property — firm when needed, invisible when not.",
    deliverables: [
      "Lobby and guest-floor patrols",
      "Event and banquet security",
      "Incident response and guest assistance",
      "CCTV and key-card system monitoring",
      "Nightlife and licensed-venue door staff",
      "Lost property and evidence handling",
    ],
    keywords: ["hotel security guards", "hospitality security services", "resort security company", "restaurant security guards", "nightclub security services"],
    image: images.guard,
  },
  {
    slug: "logistics-transport-escort",
    name: "Logistics & Transport Escort Security",
    short: "Escort and supply-chain protection for high-value freight in transit.",
    angle: "Cargo is most vulnerable between the dock doors — escorted loads arrive intact, on time and fully documented.",
    deliverables: [
      "High-value load escorts",
      "Yard-to-destination convoy security",
      "Driver check-in and route monitoring",
      "Warehouse cross-dock protection",
      "Chain-of-custody documentation",
      "Theft and hijack response protocols",
    ],
    keywords: ["cargo security escort", "transport security services", "logistics security company", "freight security escort", "supply chain security"],
    image: images.patrol,
  },
  {
    slug: "security-risk-assessment",
    name: "Security Risk & Threat Assessment",
    short: "Professional site audits that identify vulnerabilities before they become incidents.",
    angle: "Every effective security program starts on paper — a thorough risk assessment tells you exactly where your exposure lives.",
    deliverables: [
      "On-site vulnerability and threat audits",
      "Lighting, fencing and sightline review",
      "CCTV and alarm coverage gap analysis",
      "Written risk report with prioritized fixes",
      "Security program and post-order design",
      "Insurance-aligned documentation",
    ],
    keywords: ["security risk assessment", "threat assessment services", "security audit company", "site security evaluation", "security consulting services"],
    image: images.surveillance,
  },
];

export const serviceBySlug = Object.fromEntries(services.map((s) => [s.slug, s]));
